// src/pages/Features.jsx
import React from 'react';
import { WebLayout } from 'components/Layout';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'lib/auth';

export const Features = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleStart = () => {
    if (user) navigate('/app');
    else navigate('/auth/login');
  };

  return (
    <WebLayout>
      <section className="px-6 py-20 max-w-6xl mx-auto">
        {/* HEADER */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800">
            Everything You Need to Grow a Farm — Together
          </h1>
          <p className="mt-4 text-gray-500 max-w-2xl mx-auto">
            Fundo brings farmers and supporters together through community funding, smart tools, and
            trusted marketplaces — all designed for real agriculture in Africa.
          </p>
        </div>

        {/* GRID */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {/* COMMUNITY FUNDING */}
          <div className="p-8 border rounded-xl hover:shadow-md transition bg-white">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Community Funding</h3>
            <p className="text-gray-500">
              Farmers post real needs. Supporters contribute small amounts. Fundo makes sure the
              process is transparent, structured, and fair for everyone involved.
            </p>
          </div>

          {/* SMART RECORD KEEPING */}
          <div className="p-8 border rounded-xl hover:shadow-md transition bg-white">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Smart Record Keeping</h3>
            <p className="text-gray-500">
              Track daily farm activities — harvests, milk, eggs, expenses, feed, medicine — through
              the app, WhatsApp, or USSD.
            </p>
          </div>

          {/* FARM WALLET & TRANSACTIONS */}
          <div className="p-8 border rounded-xl hover:shadow-md transition bg-white">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Farm Wallet</h3>
            <p className="text-gray-500">
              Every farmer gets a secure wallet for contributions, farm expenses, activity logs, and
              direct transfers — with full transparency.
            </p>
          </div>

          {/* MARKETPLACE */}
          <div className="p-8 border rounded-xl hover:shadow-md transition bg-white">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Open Market</h3>
            <p className="text-gray-500">
              Farmers list produce and connect directly with buyers, cooperatives, and local
              markets. Transparent pricing. No middlemen.
            </p>
          </div>

          {/* SACCO CONNECTION */}
          <div className="p-8 border rounded-xl hover:shadow-md transition bg-white">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">
              Cooperative & SACCO Network
            </h3>
            <p className="text-gray-500">
              Fundo integrates with cooperatives and SACCOs so farmers can access structured
              financial pathways and community support.
            </p>
          </div>

          {/* FARM ANALYTICS */}
          <div className="p-8 border rounded-xl hover:shadow-md transition bg-white">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Farm Insights & Analytics</h3>
            <p className="text-gray-500">
              Understand production trends, sales progress, supporter engagement, and farm
              performance — all displayed in simple dashboards.
            </p>
          </div>

          {/* USSD + OFFLINE READY */}
          <div className="p-8 border rounded-xl hover:shadow-md transition bg-white">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">USSD & Offline Access</h3>
            <p className="text-gray-500">
              No smartphone? No problem. Fundo works through USSD so even rural farmers with basic
              phones can participate.
            </p>
          </div>

          {/* FARM COLLABORATION */}
          <div className="p-8 border rounded-xl hover:shadow-md transition bg-white">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Farm Team Collaboration</h3>
            <p className="text-gray-500">
              Invite workers, managers, family members, or group partners — everyone operates under
              clear roles, permissions, and activity logs.
            </p>
          </div>

          {/* PROJECT TRANSPARENCY */}
          <div className="p-8 border rounded-xl hover:shadow-md transition bg-white">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Transparent Project Cycles</h3>
            <p className="text-gray-500">
              Every supported project includes updates, milestones, expense logs, and progress
              snapshots so supporters remain confident and informed.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-20">
          <button
            onClick={handleStart}
            className="px-10 py-4 bg-green-700 text-white rounded-lg text-lg font-medium shadow hover:bg-green-800 transition"
          >
            Explore Fundo
          </button>
        </div>
      </section>
    </WebLayout>
  );
};
